<template>
  <div>
    <v-card
      min-width="1024px">
      <v-card-title>
        診断協会員一覧
        <span v-if="this.$store.state.auth.permission === 1">
          ({{ this.$store.state.auth.block.name }}ブロック)
        </span>
      </v-card-title>
      <v-card-actions>
        <v-spacer />
        <v-text-field
          v-model="keyword"
          prepend-icon="mdi-filter"
          dense
          clearable
          label="キーワード検索"
          single-line
          hide-details
          @input="onSearchKeywordChanged">
        </v-text-field>
        <v-spacer />
        <block-select
          v-if="this.$store.state.auth.permission === 2"
          v-model="blockId"
          :allow-empty="true"
          :outlined="false"
          @input="onSearchKeywordChanged">
        </block-select>
        <v-btn
          color="primary"
          @click.prevent="onAddClicked">
          新規登録
        </v-btn>
      </v-card-actions>
      <div class="d-flex justify-end pa-2">
        <v-switch
          v-model="deleted"
          label=" 削除済の会員を表示"
          :true-value="1"
          :false-value="0"
          dense
          @change="onSwitchChanged" />
      </div>
      <v-data-table
        :headers="headers"
        :items="pagination.data"
        :items-per-page="-1"
        :loading="loading"
        :hide-default-footer="true"
        disable-sort
        :item-class="itemRowClass"
        @click:row="onEditClicked">
        <template v-slot:item.has_approval_authority="props">
          {{ props.item.has_approval_authority ? 'あり' : 'なし' }}
        </template>
        <template v-slot:item.title="{ value }">
          <span
            class="d-inline-block text-truncate"
            style="max-width: 250px;">
            {{ value.name }}
          </span>
        </template>
        <template v-slot:item.identifiers="{ value }">
          <div
            v-for="(identifier, index) in value.identifiers"
            :key="index">
            <v-chip
              class="ma-2"
              x-small>
              {{ identifier.type }} ID
            </v-chip>
            {{ identifier.identifier }}
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="item.deleted_at"
            bg-color="light-blue"
            small
            color="error"
            @click.stop="onRestoreButtonClicked(item)">
            復元
          </v-btn>
        </template>
        <template #footer>
          <footer-pagination
            v-model="pagination"
            @change="onPaginationChanged" />
        </template>
      </v-data-table>
    </v-card>
    <global-footer>
      <template #left>
        <v-btn
          @click.prevent="$router.push({ name: 'index' })">
          キャンセル
        </v-btn>
      </template>
    </global-footer>

    <confirm-dialog
      :value="restoreConfirmDialog"
      title="復元確認"
      ok-text="復元する"
      cancel-text="キャンセル"
      @ok="onRestoreExecuteClicked"
      @cancel="onRestoreCancelClicked"
      @input="onRestoreCancelClicked">
      <v-card-text>
        このデータを復元します。<br />この操作は元に戻せません。
      </v-card-text>
    </confirm-dialog>
  </div>
</template>

<script>
import FooterPagination from '../FooterPagination'
import BlockSelect from '../BlockSelect'
import ConfirmDialog from '../ConfirmDialog'

export default {
  name: 'UserIndex',
  components: { BlockSelect, FooterPagination, ConfirmDialog },
  data: () => ({
    items: [],
    loading: false,
    keyword: '',
    headers: [
      { value: 'login_id', text: '登録番号' },
      { value: 'block.name', text: 'ブロック' },
      { value: 'name', text: '氏名' },
      { value: 'permission_name', text: '種別' },
      { value: 'has_approval_authority', text: '承認権限' },
      { value: 'actions', text: '', width: '8%' },
    ],
    pagination: { data: [] },
    currentPage: 1,
    targetId: '',
    blockId: '',
    blocks: [],
    deleted: 0,
    restoreConfirmDialog: false,
    restoreUserId: null,
  }),
  mounted() {
    this.pagination.current_page = this.$route.query.page || 50
    this.keyword = this.$route.query.keyword || ''
    this.pagination.per_page = window.localStorage.getItem(`pagination-count-${this.name}`) || 50
    this.fetchItems()
  },
  methods: {
    async fetchItems(page = 0) {
      const blockId = this.$store.state.auth.permission === 1
        ? this.$store.state.auth.block_id
        : this.blockId
      const { data } = await window.axios.get('/api/users', {
        params: {
          block_id: blockId,
          count: this.pagination.per_page,
          keyword: this.keyword,
          deleted: this.deleted,
          page,
        },
      })
      this.$router.replace(
        {
          name: 'userIndex',
          query: {
            page,
            block_id: blockId,
            keyword: this.keyword,
          },
        },
        () => {},
        () => {},
      )
      this.pagination = data
    },
    async fetchBlocks() {
      const { data } = await window.axios.get('/api/blocks')
      this.blocks = data.data
    },
    async onRestoreExecuteClicked() {
      try {
        await window.axios.post(
          '/api/users/restore',
          { restoreUserId: this.restoreUserId },
        )

        this.restoreConfirmDialog = false
        this.fetchItems()
      } catch ({ response }) {
        this.$store.commit('decrementLoadingCount')
        this.deletionConfirmDialog = false

        if (response.status === 422) {
          this.validationError = response.data.errors
        }
      }
    },
    onEditClicked(item) {
      if (item.deleted_at !== null) {
        this.$store.commit(
          'setSystemMessage',
          { message: '削除されたユーザです。復元してください。' },
        )
        return
      }
      this.$router.push({
        name: 'userUpdate',
        params: { id: item.id },
      })
    },
    onSearchKeywordChanged() {
      setTimeout(this.fetchItems, 500)
    },
    onAddClicked() {
      this.$router.push({ name: 'userCreate' })
    },
    onPaginationChanged(pagination) {
      this.pagination = pagination
      this.fetchItems(pagination.current_page)
      window.localStorage.setItem(`pagination-count-${this.name}`, this.pagination.per_page)
    },
    onRestoreButtonClicked(item) {
      this.restoreUserId = item.id
      this.restoreConfirmDialog = true
    },
    onRestoreCancelClicked() {
      this.restoreUserId = null
      this.restoreConfirmDialog = false
    },
    onSwitchChanged() {
      this.fetchItems(0)
    },
    itemRowClass(item) {
      return item.deleted_at !== null ? 'bg-error' : ''
    },
  },
}
</script>
